import { c as _c } from "react/compiler-runtime";
import { useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { useCopyToClipboard } from '../../../utils/copy';
import useLocalStorage from '../../hooks/useLocalStorage';
import Badge from '../base/Badge';
import Button from '../base/Button';
import { StyledCard, StyledCardSection } from '../base/Card';
import Divider from '../base/Divider';
import Link from '../base/Link';
import Text from '../base/Text';
import Tooltip from '../base/Tooltip';
import DropdownMenu from '../DropdownMenu';
import { Div } from '../helpers/StyledUtils';
import { useCodeMenuOptions } from '../JSONBrowser';
import Search from '../Search';
import { CollapsableDataSection } from './CollapsableDataSection';
const StyledHeader = styled.div<{
  dynamic_value: boolean;
  empty: boolean;
}>`
  * {
    display: inline-block;
  }
  ${Text}:first-child {
    white-space: nowrap;
  }
  ${Text}:last-child {
    word-break: break-all;
    font-style: ${({
  dynamic_value,
  empty
}) => dynamic_value || empty ? 'italic' : 'normal'};
    color: ${({
  theme,
  dynamic_value,
  empty
}) => empty ? theme.colors.on.neutral.secondary_neutral : dynamic_value ? theme.colors.on.neutral.primary : theme.colors.on.neutral.success};
  }
`;
const StyledHeaderContentHover = styled.span<{
  active: boolean;
}>`
  position: relative;
  display: inline-block;
  border-radius: ${({
  theme
}) => theme.radius.small};
  &:hover {
    background-color: ${({
  theme
}) => theme.colors.surface.base.variant_surface};
  }
  ${({
  active,
  theme
}) => active && `
    background-color: ${theme.colors.surface.base.variant_surface};
  `}
  cursor: pointer;
`;

/** Context menu config for event-gateway request headers (filter + table columns). */
const REQUEST_HEADERS_CONTEXT_CONFIG = {
  filter_field_key: 'request_headers',
  table_column_prefix: 'headers'
} as const;
const Header = t0 => {
  const $ = _c(10);
  const {
    name,
    value,
    children,
    dynamic_value,
    appended_header,
    context_menu: t1
  } = t0;
  const context_menu = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== name) {
    t2 = [name];
    $[0] = name;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const options = useCodeMenuOptions(name, value, context_menu ? REQUEST_HEADERS_CONTEXT_CONFIG : null, t2, appended_header);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      min: 208,
      max: 208
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== children || $[4] !== dynamic_value || $[5] !== value) {
    t4 = (opened, toggle) => <StyledHeaderContentHover active={opened} onClick={() => toggle(true)}><StyledHeader empty={value === ""} dynamic_value={dynamic_value}>{children}</StyledHeader></StyledHeaderContentHover>;
    $[3] = children;
    $[4] = dynamic_value;
    $[5] = value;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== options || $[8] !== t4) {
    t5 = <DropdownMenu parent_width={t3} options={options} renderToggle={t4} />;
    $[7] = options;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
};
const HighlightableText = t0 => {
  const $ = _c(9);
  const {
    text,
    search_value
  } = t0;
  const theme = useTheme();
  let t1;
  if ($[0] !== search_value || $[1] !== text || $[2] !== theme) {
    let t2;
    if ($[4] !== search_value || $[5] !== theme) {
      t2 = (part, i) => <span key={i} style={part.toLowerCase() === search_value.toLowerCase() ? {
        backgroundColor: theme.colors.surface.container.highlight
      } : {}}>{part}</span>;
      $[4] = search_value;
      $[5] = theme;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    t1 = text.split(new RegExp(`(${search_value})`, "i")).map(t2);
    $[0] = search_value;
    $[1] = text;
    $[2] = theme;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[7] !== t1) {
    t2 = <span>{t1}</span>;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
};
export const HeadersBlock = t0 => {
  const $ = _c(5);
  const {
    headers,
    appended_headers,
    search_value,
    context_menu: t1
  } = t0;
  const context_menu = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== appended_headers || $[1] !== context_menu || $[2] !== headers || $[3] !== search_value) {
    const dynamic_whitelist = ["-attempt-count", "-attempt-trigger", "-source-name", "-destination-name", "-connection-name", "-signature", "-will-retry-after", "authorization"];
    const grouped_headers = Object.entries(headers).reduce((acc, t3) => {
      const [key, value] = t3;
      if (appended_headers?.includes(key)) {
        acc.appended[key.toLowerCase()] = value;
      } else {
        acc.original[key.toLowerCase()] = value;
      }
      return acc;
    }, {
      original: {} as Record<string, string>,
      appended: {} as Record<string, string>
    });
    const filtered_original_headers = Object.entries(grouped_headers.original).filter(t4 => {
      const [key_0, value_0] = t4;
      return search_value ? key_0.toLowerCase().includes(search_value.toLowerCase()) || (value_0 || "empty").toLowerCase().includes(search_value.toLowerCase()) : true;
    });
    const filtered_appended_headers = Object.entries(grouped_headers.appended).filter(t5 => {
      const [key_1, value_1] = t5;
      return search_value ? key_1.toLowerCase().includes(search_value.toLowerCase()) || (value_1 || "empty").toLowerCase().includes(search_value.toLowerCase()) : true;
    });
    t2 = <Div>{filtered_original_headers.map(t6 => {
        const [key_2, value_2] = t6;
        return <Header key={key_2} name={key_2} value={value_2} dynamic_value={false} appended_header={false} context_menu={context_menu}><Text mono={true} size="s" key={key_2}>{search_value ? <HighlightableText text={key_2} search_value={search_value} /> : key_2}</Text>:{" "}<Text mono={true} size="s">{search_value ? <HighlightableText text={value_2} search_value={search_value} /> : value_2}</Text></Header>;
      })}{filtered_original_headers.length === 0 && filtered_appended_headers.length === 0 && <Text muted={true} text_wrap={false}>No matching headers found</Text>}{filtered_appended_headers.length > 0 && <Div flex={{
        align: "center",
        gap: 2
      }} m={{
        y: 2
      }}><Text size="xs" muted={true} semi={true} text_wrap={false}>Added by Hookdeck</Text><Divider /></Div>}{filtered_appended_headers.map(t7 => {
        const [key_3, value_3] = t7;
        return <Header key={key_3} name={key_3} value={value_3} appended_header={true} dynamic_value={dynamic_whitelist.some(whitelisted_key => key_3.includes(whitelisted_key))} context_menu={context_menu}><Text mono={true} size="s" key={key_3}>{search_value ? <HighlightableText text={key_3} search_value={search_value} /> : key_3}</Text>:{" "}{dynamic_whitelist.some(whitelisted_key_0 => key_3.includes(whitelisted_key_0)) ? <Tooltip tooltip="Header value is calculated when requests is sent. The contents of these headers may change between attempts." side="bottom"><Text mono={true} size="s">{search_value ? <HighlightableText text={value_3 || "empty"} search_value={search_value} /> : value_3 || "empty"}</Text></Tooltip> : <Text mono={true} size="s">{search_value ? <HighlightableText text={value_3 || "empty"} search_value={search_value} /> : value_3 || "empty"}</Text>}</Header>;
      })}</Div>;
    $[0] = appended_headers;
    $[1] = context_menu;
    $[2] = headers;
    $[3] = search_value;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
export const HeadersCollapsibleSection: React.FC<{
  headers: Record<string, string>;
  appended_headers?: string[];
  embedded: boolean;
  context_menu?: boolean;
}> = t0 => {
  const $ = _c(53);
  const {
    headers,
    appended_headers,
    embedded,
    context_menu: t1
  } = t0;
  const context_menu = t1 === undefined ? false : t1;
  const [collapsed, setCollapsed] = useLocalStorage("prefs:data:headers:collapsed", true);
  const theme = useTheme();
  let t2;
  if ($[0] !== headers) {
    t2 = Object.keys(headers);
    $[0] = headers;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const count = t2.length;
  const [search_value, setSearchValue] = useState("");
  const copyToClipboard = useCopyToClipboard();
  let t3;
  if ($[2] !== headers) {
    t3 = Object.entries(headers).map(_temp);
    $[2] = headers;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const headers_string = t3.join("\n");
  const t4 = embedded ? 0 : collapsed ? 0 : 8;
  let t5;
  if ($[4] !== t4) {
    t5 = {
      b: t4
    };
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const t6 = embedded && !collapsed ? theme.colors.surface.base.variant_surface_3 : undefined;
  let t7;
  if ($[6] !== t6) {
    t7 = {
      backgroundColor: t6
    };
    $[6] = t6;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      align: "center",
      gap: 2
    };
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] !== collapsed || $[10] !== setCollapsed) {
    t9 = () => setCollapsed(!collapsed);
    $[9] = collapsed;
    $[10] = setCollapsed;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Text semi={true}>Headers</Text>;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== count) {
    t11 = count !== undefined && <Badge neutral={true}>{count}</Badge>;
    $[13] = count;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== t11 || $[16] !== t9) {
    t12 = <Div flex={t8} onClick={t9}>{t10}{t11}</Div>;
    $[15] = t11;
    $[16] = t9;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      align: "center",
      gap: 2
    };
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  let t14;
  if ($[19] !== collapsed || $[20] !== copyToClipboard || $[21] !== embedded || $[22] !== headers_string) {
    t14 = !collapsed && embedded && <Button ghost={true} icon="copy" onClick={() => copyToClipboard(headers_string)} />;
    $[19] = collapsed;
    $[20] = copyToClipboard;
    $[21] = embedded;
    $[22] = headers_string;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== collapsed || $[25] !== embedded || $[26] !== setCollapsed) {
    t15 = embedded ? <Button ghost={true} icon={collapsed ? "chevron_down" : "chevron_up"} onClick={() => setCollapsed(!collapsed)} /> : <Link neutral={true} icon={collapsed ? "chevron_down" : "chevron_up"} onClick={() => setCollapsed(!collapsed)} />;
    $[24] = collapsed;
    $[25] = embedded;
    $[26] = setCollapsed;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== t14 || $[29] !== t15) {
    t16 = <Div flex={t13}>{t14}{t15}</Div>;
    $[28] = t14;
    $[29] = t15;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] !== collapsed || $[32] !== embedded || $[33] !== t12 || $[34] !== t16) {
    t17 = <CollapsableDataSection embedded={embedded} collapsed={collapsed} z_index={3}>{t12}{t16}</CollapsableDataSection>;
    $[31] = collapsed;
    $[32] = embedded;
    $[33] = t12;
    $[34] = t16;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] !== appended_headers || $[37] !== collapsed || $[38] !== context_menu || $[39] !== copyToClipboard || $[40] !== embedded || $[41] !== headers || $[42] !== headers_string || $[43] !== search_value) {
    t18 = !collapsed && <Div flex={{
      direction: "column",
      gap: 4
    }} p={embedded ? {
      x: 6,
      b: 6
    } : undefined}><Div flex={{
        gap: 3
      }}><Search w={embedded ? 100 : {
          px: 468
        }} small={true} value={search_value} onChange={setSearchValue} />{!embedded && <Button outline={true} icon="copy" onClick={() => copyToClipboard(headers_string)} />}</Div>{embedded ? <HeadersBlock headers={headers} appended_headers={appended_headers} search_value={search_value} context_menu={context_menu} /> : <StyledCard overflow_hidden={true}><StyledCardSection muted={true} p={6}><HeadersBlock headers={headers} appended_headers={appended_headers} search_value={search_value} context_menu={context_menu} /></StyledCardSection></StyledCard>}</Div>;
    $[36] = appended_headers;
    $[37] = collapsed;
    $[38] = context_menu;
    $[39] = copyToClipboard;
    $[40] = embedded;
    $[41] = headers;
    $[42] = headers_string;
    $[43] = search_value;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  let t19;
  if ($[45] !== embedded) {
    t19 = embedded && <Divider />;
    $[45] = embedded;
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  let t20;
  if ($[47] !== t17 || $[48] !== t18 || $[49] !== t19 || $[50] !== t5 || $[51] !== t7) {
    t20 = <Div m={t5} style={t7}>{t17}{t18}{t19}</Div>;
    $[47] = t17;
    $[48] = t18;
    $[49] = t19;
    $[50] = t5;
    $[51] = t7;
    $[52] = t20;
  } else {
    t20 = $[52];
  }
  return t20;
};
function _temp(t0) {
  const [key, value] = t0;
  return `${key}: ${value}`;
}